"use client";

// French copy of components/relatedWidgets (FR-1). A related widget that
// has a French page links there; any other links to its English page with an
// EN tag. Callers pass French name/description for widgets that have them.

import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { IoIosArrowBack } from "@react-icons/all-files/io/IoIosArrowBack";
import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import { IPlugin } from "@/types";
import { frHref, hasFrTwin } from "@/i18n/fr/routes";
import { EnTag } from "./langSwitch.comp";
import { StyledDiv } from "../relatedWidgets/relatedWidgets.styles";

interface IRelatedWidgetsFrProps {
  title?: string;
  apps: Pick<
    IPlugin,
    "name" | "slug" | "seoSlug" | "teaser" | "description" | "catalogImage" | "logo"
  >[];
}

export const RelatedWidgetsFr = ({
  title = "Découvrez des widgets similaires",
  apps,
}: IRelatedWidgetsFrProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, apps.length]);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.8, 300);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!apps.length) return null;

  return (
    <StyledDiv>
      <h2>{title}</h2>
      <div className="carousel-wrap">
        <button
          type="button"
          className="arrow arrow-left"
          aria-label="Faire défiler vers la gauche"
          onClick={() => scrollBy(-1)}
          disabled={!canScrollLeft}
        >
          <IoIosArrowBack size={28} />
        </button>
        <div className="track" ref={trackRef}>
          {apps.map((app) => (
            <Link
              key={app.slug}
              className="card"
              href={frHref(`/widgets/${app.seoSlug || app.slug}`)}
              hrefLang={hasFrTwin(`/widgets/${app.seoSlug || app.slug}`) ? "fr" : "en"}
              title={app.name}
            >
              <div className="image-wrapper">
                <Image
                  src={app.catalogImage || app.logo}
                  alt={`Widget\u00a0: ${app.name}`}
                  width={320}
                  height={170}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </div>
              <div className="details">
                <h3>
                  {app.name}
                  {!hasFrTwin(`/widgets/${app.seoSlug || app.slug}`) && <EnTag />}
                </h3>
                <p>{app.description || app.teaser}</p>
              </div>
            </Link>
          ))}
        </div>
        <button
          type="button"
          className="arrow arrow-right"
          aria-label="Faire défiler vers la droite"
          onClick={() => scrollBy(1)}
          disabled={!canScrollRight}
        >
          <IoIosArrowForward size={28} />
        </button>
      </div>
    </StyledDiv>
  );
};
